"use client";

import React, { useState } from "react";
import {
  Calendar,
  Clock,
  User,
  Users,
  Award,
  Presentation,
  Coffee,
  Utensils,
  MessageCircle,
  Globe,
  Lightbulb,
  TrendingUp,
  Shield,
  Package,
  X,
  Info,
  MapPin,
  Sparkles,
} from "lucide-react";
import LogoRound from "../assets/img/aqua/logo-round.png";
import AQUA_LOGO from "../assets/img/aqua_logo_v2.png";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { programData, speakerBios } from "../utils";

const Programs = () => {
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  const getSpeakerImage = (bioId) => {
    if (!bioId) return null;

    const imageMap = {
      1: "1.jpeg",
      2: "2.jpg",
      3: "3.jpg",
      4: "4.jpg",
      5: "5.jpg",
      6: "6.jpg",
      7: "7.jpg",
      8: "8.jpg",
      9: "9.jpg",
      10: "10.jpg",
      11: "11.jpg",
      12: "12.jpg",
      13: "13.jpg",
      14: "14.jpg",
      15: "15.jpg",
      16: "16.jpg",
      17: "17.jpg",
      18: "18.png",
      19: "19.jpg",
      20: "20.jpg",
      21: "21.jpg",
      22: "22.png",
      23: "23.png",
      24: "24.jpg",
      25: "25.jpg",
      26: "26.jpg",
      27: "27.png",
      28: "28.jpg",
      29: "29.jpg",
      30: "30.jpg",
      31: "31.jpg",
      32: "32.jpg",
      33: "33.jpg",
      34: "34.png",
      35: "35.jpg",
      36: "36.jpg",
      37: "37.jpg",
      38: "38.jpg",
      39: "39.jpg",
    };

    return imageMap[bioId] || null;
  };

  const openSpeakerBio = (bioId) => {
    if (bioId && speakerBios[bioId]) {
      setSelectedSpeaker({ ...speakerBios[bioId], bioId });
    }
  };

  const closeSpeakerBio = () => {
    setSelectedSpeaker(null);
  };

  const handleImageError = (bioId) => {
    console.error(`Failed to load image for bioId: ${bioId}`);
    setImageErrors((prev) => ({ ...prev, [bioId]: true }));
  };

  const BioBadge = ({ bioId }) => {
    if (!bioId) return null;
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          openSpeakerBio(bioId);
        }}
        className="group flex items-center gap-1.5 px-3 py-1 bg-white/80 backdrop-blur-sm border border-cyan-500 rounded-full text-xs font-bold text-cyan-800 hover:bg-cyan-800 hover:text-white hover:border-cyan-800 transition-all duration-200 shadow-sm"
        title="View speaker biography"
      >
        <User className="w-3 h-3 group-hover:scale-110 transition-transform" />
        <span>Bio</span>
      </button>
    );
  };

  const SpeakerPhoto = ({ bioId, speaker, size = "md" }) => {
    const speakerImage = getSpeakerImage(bioId);
    const hasError = imageErrors[bioId];

    const sizeClasses = {
      sm: "w-9 h-9",
      md: "w-11 h-11",
      lg: "w-14 h-14",
      xl: "w-16 h-16",
      "2xl": "w-24 h-24",
    };

    const iconSizes = {
      sm: "w-4 h-4",
      md: "w-5 h-5",
      lg: "w-6 h-6",
      xl: "w-8 h-8",
      "2xl": "w-10 h-10",
    };

    let imageSrc = null;
    if (speakerImage && !hasError) {
      try {
        imageSrc = require(`../assets/img/speaker-photos/${speakerImage}`);
      } catch (error) {
        console.error(`Image not found: ${speakerImage}`, error);
        setImageErrors((prev) => ({ ...prev, [bioId]: true }));
      }
    }

    return (
      <div
        className={`${sizeClasses[size]} rounded-full overflow-hidden bg-white border-2 border-cyan-400 shadow-md flex-shrink-0 flex items-center justify-center`}
      >
        {imageSrc && !hasError ? (
          <img
            src={imageSrc}
            alt={speaker || "Speaker"}
            className="w-full h-full object-cover"
            onError={() => handleImageError(bioId)}
          />
        ) : (
          <User className={`${iconSizes[size]} text-cyan-400`} />
        )}
      </div>
    );
  };

  const SessionItem = ({ title, speaker, role, bioId, type }) => {
    return (
      <div className="relative pl-6 pb-6 border-l-2 border-cyan-600/30 last:border-0 last:pb-0">
        <div className="absolute left-[-6px] top-0 w-3 h-3 rounded-full bg-cyan-700 ring-4 ring-cyan-200/50"></div>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 group">
          <div className="flex-1">
            {type && (
              <span className="inline-block px-2 py-0.5 mb-1.5 text-[10px] uppercase tracking-wider font-extrabold text-white bg-cyan-700 rounded shadow-sm">
                {type}
              </span>
            )}
            {title && (
              <h5 className="font-bold text-cyan-950 leading-snug mb-1 text-lg">
                {title}
              </h5>
            )}
            <div className="flex items-center gap-3 mt-2">
              <SpeakerPhoto bioId={bioId} speaker={speaker} size="md" />
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-bold text-cyan-900">
                  {speaker}
                </span>
                {bioId && <BioBadge bioId={bioId} />}
              </div>
            </div>
            {role && (
              <p className="text-xs font-semibold text-cyan-800 mt-2 leading-relaxed max-w-2xl bg-white/30 p-2 rounded-lg inline-block border border-cyan-300/30">
                {role}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const SpeakerBioModal = () => {
    if (!selectedSpeaker) return null;

    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ zIndex: 9999 }}
      >
        <div
          className="absolute inset-0 bg-cyan-950/60 backdrop-blur-md transition-opacity"
          onClick={closeSpeakerBio}
        />
        <div
          className="relative bg-gradient-to-b from-cyan-100 to-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-slideUp border border-cyan-200"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-8">
            <button
              onClick={closeSpeakerBio}
              className="absolute right-4 top-4 text-white/80 hover:text-white hover:bg-white/20 rounded-full p-2 transition-all z-10"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              <SpeakerPhoto
                bioId={selectedSpeaker.bioId}
                speaker={selectedSpeaker.name}
                size="2xl"
              />

              <div className="flex-1 text-center sm:text-left">
                <h2 className="text-2xl  font-extrabold text-white mb-2 drop-shadow-md">
                  {selectedSpeaker.name}
                </h2>
                <p className="text-cyan-50 text-sm md:text-base font-bold leading-relaxed">
                  {selectedSpeaker.role}
                </p>
              </div>
            </div>
          </div>

          <div className="p-6 overflow-y-auto custom-scrollbar flex-1 bg-white/50">
            {selectedSpeaker.bio ? (
              <div className="prose prose-sm prose-cyan max-w-none">
                <p className="text-cyan-950 font-medium leading-relaxed text-base">
                  {selectedSpeaker.bio}
                </p>
              </div>
            ) : (
              <div className="text-center py-16 flex flex-col items-center justify-center text-cyan-400">
                <div className="w-16 h-16 rounded-full bg-cyan-50 flex items-center justify-center mb-4">
                  <Info className="w-8 h-8 text-cyan-300" />
                </div>
                <p className="text-lg font-bold text-cyan-700">
                  Biography Coming Soon
                </p>
                <p className="text-sm text-cyan-600 mt-1">
                  Detailed information will be added shortly
                </p>
              </div>
            )}
          </div>

          <div className="px-6 py-4 bg-cyan-50 border-t border-cyan-200">
            <button
              onClick={closeSpeakerBio}
              className="w-full py-3 rounded-xl bg-cyan-600 text-white font-bold text-sm hover:bg-cyan-700 transition-colors shadow-lg hover:shadow-cyan-500/50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const BreakCard = ({ icon: Icon, title, colorClass, bgClass, textClass }) => (
    <div
      className={`rounded-xl p-4 flex items-center justify-center gap-3 border-2 bg-white/90 backdrop-blur shadow-md ${colorClass}`}
    >
      <Icon className={`w-6 h-6 ${textClass}`} />
      <span className={`font-bold text-base ${textClass}`}>{title}</span>
    </div>
  );

  const SessionCard = ({ session, dayNumber, showBreakAfter }) => {
    const IconComponent = session.icon;

    return (
      <>
        <div className="bg-gradient-to-b from-cyan-300 to-cyan-200 rounded-2xl border border-white/40 shadow-xl hover:shadow-2xl hover:scale-[1.01] transition-all duration-300 p-6 sm:p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none"></div>

          <div className="flex items-start gap-4 mb-6 border-b border-cyan-400/30 pb-6 relative z-10">
            <div className="w-14 h-14 rounded-2xl bg-white/80 backdrop-blur text-cyan-700 shadow-inner flex items-center justify-center shrink-0 border border-white">
              <IconComponent className="w-7 h-7" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl sm:text-2xl font-extrabold text-cyan-950 mb-2 drop-shadow-sm">
                {session.title}
              </h3>

              {session.moderator && (
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <div className="px-2 py-1 bg-cyan-800 rounded text-xs font-bold text-cyan-100 uppercase tracking-wide shadow-sm">
                    Moderator
                  </div>
                  <div className="flex items-center gap-2 bg-white/30 px-3 py-1 rounded-full border border-white/40">
                    <SpeakerPhoto
                      bioId={session.bioId}
                      speaker={session.moderator}
                      size="sm"
                    />
                    <span className="text-sm font-bold text-cyan-900">
                      {session.moderator}
                    </span>
                    <BioBadge bioId={session.bioId} />
                  </div>
                </div>
              )}
            </div>
          </div>

          {(session.speakers || session.panelMembers) && (
            <div className="flex items-center gap-4 mb-6 relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg transform -skew-x-6 shadow-md text-white">
                <Users className="w-4 h-4 transform skew-x-6" />
                <span className="text-sm font-bold uppercase tracking-wider transform skew-x-6">
                  Panel Discussion
                </span>
              </div>
              <div className="h-0.5 flex-1 bg-gradient-to-r from-orange-300/50 to-transparent"></div>
            </div>
          )}

          <div className="space-y-6 relative z-10">
            {session.items && (
              <div className="mt-2 pl-2">
                {session.items.map((item, idx) => (
                  <SessionItem
                    key={idx}
                    type={item.type}
                    title={item.title}
                    speaker={item.speaker}
                    role={item.role}
                    bioId={item.bioId}
                  />
                ))}
              </div>
            )}

            {session.talks && (
              <div className="grid gap-4">
                {session.talks.map((talk, idx) => (
                  <div
                    key={idx}
                    className="group p-4 rounded-xl bg-white/40 border border-white/60 hover:bg-white/60 hover:shadow-md transition-all duration-200"
                  >
                    <h4 className="text-base font-bold text-cyan-950 mb-3 leading-snug">
                      {talk.title}
                    </h4>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <SpeakerPhoto
                          bioId={talk.bioId}
                          speaker={talk.speaker}
                          size="md"
                        />
                        <span className="text-sm font-bold text-cyan-900">
                          {talk.speaker}
                        </span>
                      </div>
                      <BioBadge bioId={talk.bioId} />
                    </div>
                  </div>
                ))}
              </div>
            )}

            {session.speakers && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {session.speakers.map((speaker, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between p-3 rounded-xl bg-white/40 border border-white/60 hover:border-cyan-500 hover:bg-white/70 transition-all shadow-sm"
                  >
                    <div className="flex items-center gap-3">
                      <SpeakerPhoto
                        bioId={speaker.bioId}
                        speaker={speaker.name}
                        size="sm"
                      />
                      <span className="text-sm font-bold text-cyan-900">
                        {speaker.name}
                      </span>
                    </div>
                    <BioBadge bioId={speaker.bioId} />
                  </div>
                ))}
              </div>
            )}

            {session.panelMembers && (
              <div className="bg-white/20 p-4 rounded-2xl border border-white/30">
                <div className="flex items-center gap-2 mb-4 text-cyan-900 font-extrabold text-sm uppercase tracking-wider">
                  <Users className="w-5 h-5 text-cyan-700" />
                  <span>Distinguished Panel</span>
                </div>
                <div className="grid gap-2">
                  {session.panelMembers.map((member, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-xl bg-white/60 border border-white hover:border-cyan-400 transition-colors shadow-sm"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        <SpeakerPhoto
                          bioId={member.bioId}
                          speaker={member.name}
                          size="md"
                        />
                        <div>
                          <div className="text-sm font-bold text-cyan-950">
                            {member.name}
                          </div>
                          <div className="text-xs font-semibold text-cyan-700 mt-0.5 line-clamp-1">
                            {member.affiliation}
                          </div>
                        </div>
                      </div>
                      <BioBadge bioId={member.bioId} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Render Break After Session */}
        {showBreakAfter && (
          <div className="my-6">
            {showBreakAfter === "tea" && (
              <BreakCard
                icon={Coffee}
                title="Tea Break"
                bgClass="bg-white"
                colorClass="border-amber-400"
                textClass="text-amber-700"
              />
            )}
            {showBreakAfter === "lunch" && (
              <BreakCard
                icon={Utensils}
                title="Lunch Break"
                bgClass="bg-white"
                colorClass="border-emerald-500"
                textClass="text-emerald-700"
              />
            )}
            {showBreakAfter === "networking" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <BreakCard
                  icon={Utensils}
                  title="Lunch Break"
                  bgClass="bg-white"
                  colorClass="border-emerald-500"
                  textClass="text-emerald-700"
                />
                <BreakCard
                  icon={MessageCircle}
                  title="Networking"
                  bgClass="bg-white"
                  colorClass="border-blue-500"
                  textClass="text-blue-700"
                />
              </div>
            )}
            {showBreakAfter === "vote-of-thanks" && (
              <BreakCard
                icon={Award}
                title="Vote of Thanks"
                bgClass="bg-white"
                colorClass="border-pink-500"
                textClass="text-pink-700"
              />
            )}
            {showBreakAfter === "happy-hours" && (
              <BreakCard
                icon={Sparkles}
                title="Happy Hours"
                bgClass="bg-white"
                colorClass="border-purple-500"
                textClass="text-purple-700"
              />
            )}
          </div>
        )}
      </>
    );
  };

  const DaySection = ({ dayNumber, date, sessions }) => (
    <div className="mb-12">
      <div className="bg-gradient-to-r from-cyan-700 to-blue-800 text-white rounded-2xl shadow-xl p-6 sm:p-8 mb-8 border border-cyan-400/30 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -right-10 -top-10 w-64 h-64 bg-cyan-300 rounded-full blur-3xl"></div>
          <div className="absolute -left-10 -bottom-10 w-64 h-64 bg-blue-300 rounded-full blur-3xl"></div>
        </div>

        <div className="flex items-center gap-6 relative z-10">
          <div className="w-20 h-20 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-4xl font-extrabold shadow-inner text-cyan-100">
            {dayNumber.toString().padStart(2, "0")}
          </div>
          <div className="flex-1">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-1 tracking-tight text-white drop-shadow-md">
              Day {dayNumber === 1 ? "One" : "Two"}
            </h2>
            <div className="flex items-center gap-2 text-cyan-100 font-semibold">
              <Clock className="w-5 h-5" />
              <span className="text-lg">{date}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {sessions.map((session, index) => {
          // Determine which break to show after each session
          let breakType = null;

          if (dayNumber === 1) {
            // Day 1 breaks
            if (index === 1) breakType = "tea"; // After Keynote Speech
            if (index === 2) breakType = "lunch"; // After Session 1
            if (index === 3) breakType = "tea"; // After Session 2
          } else {
            // Day 2 breaks
            if (index === 0) breakType = "tea"; // After first session
            // Removed tea break after last session (Session 6)
          }

          return (
            <SessionCard
              key={session.id}
              session={session}
              dayNumber={dayNumber}
              showBreakAfter={breakType}
            />
          );
        })}

        {/* Add the break schedule card at the end of Day 2 */}
        {dayNumber === 2 && (
          <div className="mt-8">
            <BreakScheduleCard />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <div className="w-full md:w-[90%] md:mt-[60px] mt-[0px] mx-auto bg-white/80 backdrop-blur-md rounded-b-2xl shadow-lg border-b border-white/50 overflow-hidden font-sans">
        <div className="p-4 md:p-8 flex flex-row md:items-center items-end justify-between gap-4">
          <div className="flex flex-col px-2 items-start w-[70%] md:w-auto">
            <div className="relative">
              <img
                src={AQUA_LOGO}
                className="w-[200px] md:w-[380px] object-contain drop-shadow-sm"
                alt="Aqua India Logo"
              />
            </div>
          </div>

          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <img
              src={LogoRound}
              alt="Temple"
              className="w-20 md:w-28 object-contain drop-shadow-md"
            />
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white text-center py-3 px-4 text-xs md:text-sm font-extrabold uppercase tracking-widest mt-2 md:mt-0 shadow-inner">
          30 & 31 JANUARY, 2026 - Radisson Blu Resort, Temple Bay Mamallapuram,
          Tamil Nadu, India
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <DaySection
          dayNumber={1}
          date={programData.day1.date}
          sessions={programData.day1.sessions}
        />

        <DaySection
          dayNumber={2}
          date={programData.day2.date}
          sessions={programData.day2.sessions}
        />
      </div>

      <SpeakerBioModal />

      <Footer />
    </div>
  );
};

export default Programs;

const BreakScheduleCard = () => (
  <div className="rounded-2xl p-1 relative overflow-hidden">
    <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl pointer-events-none"></div>

    <div className="relative z-10 space-y-4">
      {/* Panel Discussion Box */}
      <div className="bg-white/90 backdrop-blur rounded-xl p-5 flex items-center gap-3 border-2 border-cyan-500 shadow-md">
        <Users className="w-6 h-6 text-cyan-700 flex-shrink-0" />
        <span className="font-bold text-base text-cyan-700">
          Panel Discussion
        </span>
      </div>

      {/* Vote of Thanks Box */}
      <div className="bg-white/90 backdrop-blur rounded-xl p-5 flex items-center gap-3 border-2 border-pink-500 shadow-md">
        <Award className="w-6 h-6 text-pink-700 flex-shrink-0" />
        <span className="font-bold text-base text-pink-700">
          Vote of Thanks
        </span>
      </div>

      {/* Happy Hour Box */}
      <div className="bg-white/90 backdrop-blur rounded-xl p-5 flex items-center gap-3 border-2 border-purple-500 shadow-md">
        <Sparkles className="w-6 h-6 text-purple-700 flex-shrink-0" />
        <span className="font-bold text-base text-purple-700">Happy Hour</span>
      </div>
    </div>
  </div>
);
